import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Calculator, ChevronDown, ExternalLink, LogIn, LogOut, User as UserIcon } from "lucide-react";
import { CATEGORIES } from "../../config/categories";
import { getCalculatorsByCategory } from "../../config/calculators";
import { MAIN_SITE_URL } from "../../config/articles";
import { Icon } from "../common/Icon";
import { analytics } from "../../lib/analytics/ga4";
import { useAuth } from "../../context/auth";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const { user, logout, openAuthModal } = useAuth();

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-brand ${isActive ? "text-brand" : "text-foreground/80"}`;

  return (
    <header className="sticky top-0 z-50 glass-nav border-b border-border/70">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white">
            <Calculator className="h-5 w-5" />
          </span>
          <span className="font-heading text-lg font-700 font-bold tracking-tight text-brand">
            MyFinancesGuru <span className="text-action">Tools</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          <div className="relative" onMouseEnter={() => setToolsOpen(true)} onMouseLeave={() => setToolsOpen(false)}>
            <button data-testid="nav-calculators" className="flex items-center gap-1 text-sm font-medium text-foreground/80 transition-colors hover:text-brand">
              Calculators <ChevronDown className="h-4 w-4" />
            </button>
            {toolsOpen && (
              <div className="absolute left-1/2 top-full w-[560px] -translate-x-1/2 pt-3">
                <div className="grid grid-cols-2 gap-1 rounded-xl border border-border bg-card p-3 shadow-xl">
                  {CATEGORIES.map((cat) => (
                    <div key={cat.id} className="rounded-lg p-2">
                      <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        <Icon name={cat.icon} className="h-3.5 w-3.5 text-brand" /> {cat.name}
                      </div>
                      {getCalculatorsByCategory(cat.id).map((c) => (
                        <Link key={c.slug} to={`/calculators/${c.slug}`} data-testid={`menu-${c.slug}`}
                          className="block rounded-md px-2 py-1 text-sm text-foreground/80 transition-colors hover:bg-secondary hover:text-brand">
                          {c.name}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/calculators" className={linkCls} data-testid="nav-all-tools">All Tools</NavLink>
          <NavLink to="/products" className={linkCls} data-testid="nav-products">Products</NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-about">About</NavLink>
          <a href={MAIN_SITE_URL} target="_blank" rel="noopener noreferrer" onClick={() => analytics.mainSiteClick("header")}
            className="flex items-center gap-1 text-sm font-medium text-foreground/80 transition-colors hover:text-brand" data-testid="nav-main-site">
            Blog <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link to="/guru-plus" data-testid="nav-guru-plus" className="text-sm font-semibold text-brand hover:text-action">Guru Plus</Link>
          {user ? (
            <div className="relative" onMouseEnter={() => setUserOpen(true)} onMouseLeave={() => setUserOpen(false)}>
              <button data-testid="user-menu-button" className="flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm font-medium text-foreground/80 hover:text-brand">
                {user.picture ? <img src={user.picture} alt="" className="h-6 w-6 rounded-full" /> : <UserIcon className="h-4 w-4" />}
                <span className="max-w-[120px] truncate">{user.name || user.email}</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              {userOpen && (
                <div className="absolute right-0 top-full w-48 pt-2">
                  <div className="rounded-xl border border-border bg-card p-2 shadow-xl">
                    <p className="truncate px-3 py-1 text-xs text-muted-foreground">{user.email}</p>
                    <Link to="/dashboard" data-testid="nav-dashboard" className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground/80 hover:bg-secondary hover:text-brand">
                      <UserIcon className="h-4 w-4" /> My dashboard
                    </Link>
                    <button onClick={logout} data-testid="logout-button"
                      className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground/80 hover:bg-secondary hover:text-brand">
                      <LogOut className="h-4 w-4" /> Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => openAuthModal()} data-testid="signin-google"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand px-4 py-2 text-sm font-semibold text-brand transition-colors hover:bg-brand/5">
              <LogIn className="h-4 w-4" /> Sign in
            </button>
          )}
          <Link to="/products" data-testid="header-cta"
            className="rounded-full bg-action px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-action-hover">
            Get the Planners
          </Link>
        </div>

        <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-card lg:hidden" data-testid="mobile-menu">
          <div className="space-y-4 px-4 py-4">
            {CATEGORIES.map((cat) => (
              <div key={cat.id}>
                <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">{cat.name}</div>
                {getCalculatorsByCategory(cat.id).map((c) => (
                  <Link key={c.slug} to={`/calculators/${c.slug}`} onClick={() => setOpen(false)}
                    className="block py-1.5 text-sm text-foreground/80">{c.name}</Link>
                ))}
              </div>
            ))}
            <div className="flex flex-col gap-2 border-t border-border pt-3">
              <Link to="/products" onClick={() => setOpen(false)} className="text-sm font-medium">Products</Link>
              <Link to="/about" onClick={() => setOpen(false)} className="text-sm font-medium">About</Link>
              {user ? (
                <button onClick={() => { setOpen(false); logout(); }} data-testid="mobile-logout" className="flex items-center gap-2 text-sm font-medium text-brand">
                  <LogOut className="h-4 w-4" /> Sign out ({user.name || user.email})
                </button>
              ) : (
                <button onClick={() => { setOpen(false); openAuthModal(); }} data-testid="mobile-signin" className="flex items-center gap-2 text-sm font-medium text-brand">
                  <LogIn className="h-4 w-4" /> Sign in
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
